import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NAV, LOGOUT } from "@/constants/testIds";
import { Menu, X, Heart } from "lucide-react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const linkClass = ({ isActive }) =>
        `font-sans text-sm tracking-wider transition-colors ${
            isActive ? "text-navy" : "text-navy-500 hover:text-navy"
        }`;

    return (
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-navy-100">
            <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
                <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
                    <div className="h-9 w-9 rounded-full bg-navy flex items-center justify-center transition-transform group-hover:scale-105">
                        <Heart className="h-4 w-4 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="leading-none">
                        <div className="font-serif text-2xl text-navy tracking-tight">HolyMarry</div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-navy-400 -mt-0.5">Orthodox Matrimony</div>
                    </div>
                </Link>

                <div className="hidden lg:flex items-center gap-8">
                    {user && (
                        <>
                            <NavLink to="/search" className={linkClass} data-testid={NAV.search}>Browse</NavLink>
                            <NavLink to="/shortlist" className={linkClass} data-testid={NAV.shortlist}>Shortlist</NavLink>
                            <NavLink to="/messages" className={linkClass} data-testid={NAV.messages}>Messages</NavLink>
                            <NavLink to="/notifications" className={linkClass} data-testid={NAV.notifications}>Notifications</NavLink>
                            <NavLink to="/premium" className={linkClass} data-testid={NAV.premium}>Premium</NavLink>
                            {user.role === "admin" && (
                                <NavLink to="/admin" className={linkClass} data-testid={NAV.admin}>Admin</NavLink>
                            )}
                        </>
                    )}
                    {!user && (
                        <>
                            <NavLink to="/search" className={linkClass} data-testid={NAV.search}>Browse</NavLink>
                            <a href="#how" className="font-sans text-sm tracking-wider text-navy-500 hover:text-navy">How it works</a>
                            <a href="#stories" className="font-sans text-sm tracking-wider text-navy-500 hover:text-navy">Stories</a>
                            <a href="https://www.orthodoxtv.in" target="_blank" rel="noopener noreferrer" data-testid="nav-orthodoxtv-link" className="font-sans text-sm tracking-wider text-navy-500 hover:text-gold-700 inline-flex items-center gap-1">
                                Orthodox TV <span className="text-[10px] opacity-70">↗</span>
                            </a>
                        </>
                    )}
                </div>

                <div className="hidden lg:flex items-center gap-3">
                    {user ? (
                        <>
                            <Link
                                to="/dashboard"
                                data-testid={NAV.profile}
                                className="font-sans text-sm tracking-wider text-navy hover:text-gold-600"
                            >
                                Hi, {user.name?.split(" ")[0]}
                            </Link>
                            <button
                                onClick={handleLogout}
                                data-testid={LOGOUT.button}
                                className="font-sans text-xs uppercase tracking-[0.15em] bg-navy text-white rounded-lg px-5 py-2.5 hover:bg-navy-700 active:scale-95 transition-all"
                            >
                                Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid={NAV.login}
                                className="font-sans text-sm tracking-wider text-navy hover:text-gold-600"
                            >
                                Sign In
                            </Link>
                            <Link
                                to="/register"
                                data-testid={NAV.register}
                                className="font-sans text-xs uppercase tracking-[0.15em] bg-gold text-navy rounded-lg px-5 py-2.5 hover:bg-gold-600 hover:text-white active:scale-95 transition-all shadow-md shadow-gold/20"
                            >
                                Register Free
                            </Link>
                        </>
                    )}
                </div>

                <button
                    className="lg:hidden p-2 text-navy"
                    onClick={() => setOpen(!open)}
                    data-testid={NAV.mobileToggle}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </nav>

            {open && (
                <div className="lg:hidden border-t border-navy-100 px-6 py-4 space-y-3 bg-white">
                    {user ? (
                        <>
                            <Link to="/dashboard" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>My Profile</Link>
                            <Link to="/search" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Browse</Link>
                            <Link to="/shortlist" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Shortlist</Link>
                            <Link to="/messages" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Messages</Link>
                            <Link to="/notifications" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Notifications</Link>
                            <Link to="/premium" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Premium</Link>
                            {user.role === "admin" && <Link to="/admin" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Admin</Link>}
                            <button onClick={handleLogout} className="block py-1 font-sans text-sm text-navy">Sign out</button>
                        </>
                    ) : (
                        <>
                            <Link to="/search" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Browse Profiles</Link>
                            <Link to="/login" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Sign In</Link>
                            <Link to="/register" className="block py-1 font-sans text-sm text-navy" onClick={() => setOpen(false)}>Register Free</Link>
                        </>
                    )}
                </div>
            )}
        </header>
    );
}
